import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Phone, PhoneOff, Mic, MicOff, Loader2 } from 'lucide-react';
import Vapi from '@vapi-ai/web';

const VAPI_PUBLIC_KEY = process.env.REACT_APP_VAPI_PUBLIC_KEY;
const VAPI_ASSISTANT_ID = process.env.REACT_APP_VAPI_ASSISTANT_ID;

const CALL_STATES = {
  IDLE: 'idle',
  CONNECTING: 'connecting',
  CONNECTED: 'connected',
  ENDED: 'ended',
};

export default function VapiVoiceButton() {
  const [callState, setCallState] = useState(CALL_STATES.IDLE);
  const [error, setError] = useState(null);
  const [micDenied, setMicDenied] = useState(false);
  const vapiRef = useRef(null);

  useEffect(() => {
    if (!VAPI_PUBLIC_KEY) return;
    const vapi = new Vapi(VAPI_PUBLIC_KEY);
    vapiRef.current = vapi;

    vapi.on('call-start', () => {
      setCallState(CALL_STATES.CONNECTED);
      setError(null);
    });

    vapi.on('call-end', () => {
      setCallState(CALL_STATES.IDLE);
    });

    vapi.on('error', (err) => {
      setError(err?.message || 'Call failed. Please try again.');
      setCallState(CALL_STATES.IDLE);
    });

    return () => {
      vapi.stop();
    };
  }, []);

  const startCall = useCallback(async () => {
    if (!vapiRef.current || !VAPI_ASSISTANT_ID) return;

    setError(null);
    setMicDenied(false);

    try {
      await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      setMicDenied(true);
      setError('Microphone access denied. Please allow mic access and try again.');
      return;
    }

    setCallState(CALL_STATES.CONNECTING);
    try {
      await vapiRef.current.start(VAPI_ASSISTANT_ID);
    } catch (err) {
      setError(err?.message || 'Failed to start call.');
      setCallState(CALL_STATES.IDLE);
    }
  }, []);

  const stopCall = useCallback(() => {
    if (vapiRef.current) {
      vapiRef.current.stop();
      setCallState(CALL_STATES.IDLE);
    }
  }, []);

  const toggleCall = () => {
    if (callState === CALL_STATES.CONNECTED || callState === CALL_STATES.CONNECTING) {
      stopCall();
    } else {
      startCall();
    }
  };

  const isActive = callState === CALL_STATES.CONNECTED || callState === CALL_STATES.CONNECTING;

  return (
    <div className="fixed bottom-6 left-6 z-[9998] flex flex-col items-start gap-2" data-testid="vapi-voice-widget">
      {/* Status / Error tooltip */}
      {(error || callState === CALL_STATES.CONNECTED) && (
        <div
          data-testid="vapi-status-message"
          className={`px-4 py-2 rounded-xl text-sm font-medium shadow-lg backdrop-blur-sm max-w-[260px] ${
            error
              ? 'bg-red-500/90 text-white'
              : 'bg-emerald-500/90 text-white'
          }`}
        >
          {error ? (
            <div className="flex items-center gap-2">
              <MicOff className="w-4 h-4 shrink-0" />
              <span>{error}</span>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <Mic className="w-4 h-4 shrink-0 animate-pulse" />
              <span>Connected — Talk to Simon</span>
            </div>
          )}
        </div>
      )}

      {/* Main button */}
      <button
        onClick={toggleCall}
        data-testid="vapi-call-button"
        disabled={callState === CALL_STATES.CONNECTING}
        className={`group relative flex items-center gap-3 rounded-full shadow-2xl transition-all duration-500 hover:-translate-y-1 active:scale-95 disabled:cursor-wait ${
          isActive
            ? 'bg-red-500 hover:bg-red-600 text-white pl-5 pr-6 py-4'
            : 'bg-[#4AC5DB] hover:bg-[#3AB0C5] text-white pl-5 pr-6 py-4 shadow-[#4AC5DB]/30'
        }`}
        aria-label={isActive ? 'End call with Simon' : 'Talk to Simon'}
      >
        {callState === CALL_STATES.CONNECTING ? (
          <Loader2 className="w-6 h-6 animate-spin" />
        ) : isActive ? (
          <PhoneOff className="w-6 h-6" />
        ) : (
          <Phone className="w-6 h-6" />
        )}
        <span className="font-semibold text-sm whitespace-nowrap">
          {callState === CALL_STATES.CONNECTING
            ? 'Connecting...'
            : isActive
            ? 'End Call'
            : 'Talk to Simon'}
        </span>

        {/* Pulse ring when connected */}
        {callState === CALL_STATES.CONNECTED && (
          <>
            <span className="absolute inset-0 rounded-full bg-red-500/30 animate-ping" />
            <span className="absolute inset-0 rounded-full bg-red-500/20 animate-pulse" />
          </>
        )}
      </button>
    </div>
  );
}
